import React, { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import TeacherPointAwards from '../../admin/points/TeacherPointAwards';

export default function TeacherHomeBonus({ loadRoster, api }) {
  const [students, setStudents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('teacher_bonus');
  const locked = useRef(false);
  const active = useRef(false);
  const bonusLauncher = useRef(null);
  const appreciationLauncher = useRef(null);
  const launcher = useRef(null);
  const restoreFocus = useRef(false);

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  useEffect(() => {
    // Wait for the shared dialog's focus trap to unmount before restoring focus.
    if (students === null && restoreFocus.current) {
      restoreFocus.current = false;
      launcher.current?.focus();
    }
  }, [students]);

  const openAward = async (kind) => {
    if (locked.current || students !== null) return;
    locked.current = true;
    launcher.current = kind === 'teacher_appreciation' ? appreciationLauncher.current : bonusLauncher.current;
    setMode(kind);
    setLoading(true); setError('');
    try {
      const roster = await loadRoster();
      if (!Array.isArray(roster?.students)) throw new Error('Öğrenci listesi doğrulanamadı. Lütfen tekrar deneyin.');
      if (active.current) setStudents(roster.students);
    } catch (err) {
      if (active.current) setError(err.message || 'Öğrenciler yüklenemedi. Lütfen tekrar deneyin.');
    } finally {
      locked.current = false;
      if (active.current) setLoading(false);
    }
  };

  const closeBonus = () => {
    restoreFocus.current = true;
    setStudents(null);
  };

  return <Box className="teacher-home-bonus" sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: 0 }}>
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
    {['teacher_bonus', 'teacher_appreciation'].map(kind => <Button key={kind}
      ref={kind === 'teacher_bonus' ? bonusLauncher : appreciationLauncher} disableRipple
      onClick={() => openAward(kind)} disabled={loading || students !== null} aria-busy={loading && mode === kind}
      startIcon={loading && mode === kind ? <CircularProgress size={22} color="inherit" aria-hidden="true" />
        : kind === 'teacher_bonus' ? <StarRoundedIcon sx={{ color: '#a16207' }} /> : <span aria-hidden="true">👏</span>}
      sx={{ width: { xs: '100%', md: 'auto' }, minHeight: { xs: 48, md: 56 }, px: 2, borderRadius: 3,
        textTransform: 'none', fontWeight: 800, fontSize: 16, whiteSpace: 'nowrap',
        color: '#4c1d95', bgcolor: '#f0e7ff', border: '1px solid #b7a5cf',
        '&:hover': { bgcolor: '#e9d5ff' }, '&:active': { bgcolor: '#dfc5fa' },
        '&:focus-visible': { outline: '3px solid #4c1d95', outlineOffset: 3 },
        '&.Mui-disabled': { color: '#625477', bgcolor: '#e5e1eb' } }}>
      {loading && mode === kind ? 'Öğrenciler yükleniyor…' : kind === 'teacher_bonus' ? 'Öğretmen Bonusu' : 'Öğretmen Takdiri'}
    </Button>)}
    </Box>
    {loading && <Box role="status" sx={{ mt: 1, fontSize: 14, color: '#4c1d95' }}>Sınıfın öğrenci listesi yükleniyor.</Box>}
    {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    {students !== null && <TeacherPointAwards api={api} students={students} hideLauncher
      initialBonusOpen={mode === 'teacher_bonus'} initialAppreciationOpen={mode === 'teacher_appreciation'}
      onFlowClose={closeBonus} onAward={() => { /* Points board fetches fresh balances when opened. */ }} />}
  </Box>;
}
